/**
 * admin/makua/kit.js — the list and editing behaviour every V2.1 screen
 * shares (handoff §4, "Universal standards"):
 *
 *   useStored        a filter or view that's remembered in this browser
 *   useSort / SortTh sortable columns, announced to screen readers
 *   useReorder       drag-to-reorder, with Move up / Move down for keyboards
 *   useUnsaved       "you have unsaved changes" before leaving
 *   Gallery          a photo gallery: upload several, primary, order,
 *                    replace, remove
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { galleryAPI, mediaUrl } from "../../services/api";

/* ── Remembered state ───────────────────────────────────────── */
export function useStored(key, initial) {
  const [value, setValue] = useState(() => {
    try { const v = localStorage.getItem(`admin:${key}`); return v == null ? initial : JSON.parse(v); } catch (_) { return initial; }
  });
  useEffect(() => { try { localStorage.setItem(`admin:${key}`, JSON.stringify(value)); } catch (_) { /* private mode */ } }, [key, value]);
  return [value, setValue];
}

/* ── Sorting ────────────────────────────────────────────────── */
const cmp = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
};

/**
 * useSort(rows, { key: row => value }, "defaultKey")
 * → { rows, sort, setSort } — sort = { key, dir }; null key keeps the given order.
 */
export function useSort(rows, accessors, initial = null, storeKey = null) {
  const [stored, setStored] = useStored(storeKey || "sort:none", { key: initial, dir: "asc" });
  const [local, setLocal] = useState({ key: initial, dir: "asc" });
  const sort = storeKey ? stored : local;
  const setSort = storeKey ? setStored : setLocal;
  const sorted = useMemo(() => {
    const get = accessors[sort.key];
    if (!get) return rows;
    const out = [...rows].sort((a, b) => cmp(get(a), get(b)));
    return sort.dir === "desc" ? out.reverse() : out;
  }, [rows, sort, accessors]);
  return { rows: sorted, sort, setSort };
}

/** A sortable column heading. Click again to reverse; a third time clears. */
export function SortTh({ k, sort, setSort, children, className = "" }) {
  const on = sort.key === k;
  const next = () => setSort(!on ? { key: k, dir: "asc" } : sort.dir === "asc" ? { key: k, dir: "desc" } : { key: null, dir: "asc" });
  return (
    <th className={className} aria-sort={on ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" className={`ops-sort ${on ? "is-on" : ""}`} onClick={next}>
        {children}<span aria-hidden="true" className="ops-sort-mark">{on ? (sort.dir === "asc" ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );
}

/* ── Drag to reorder ────────────────────────────────────────── */
/**
 * useReorder(items, save) — items have `id`. save(ids) persists the order
 * and may return a promise; on failure the order goes back.
 * rowProps(id) goes on each draggable row; moveBy(id, ±1) for the buttons.
 */
export function useReorder(items, save) {
  const [order, setOrder] = useState(() => items.map((i) => i.id));
  const [dragging, setDragging] = useState(null);
  const [over, setOver] = useState(null);
  const key = items.map((i) => i.id).join(",");
  useEffect(() => { setOrder(items.map((i) => i.id)); }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const ordered = order.map((id) => byId.get(id)).filter(Boolean);

  const commit = useCallback(async (next) => {
    const before = order;
    setOrder(next);
    try { await save(next); } catch (err) { setOrder(before); toast.error(err.message || "Couldn't save the order."); }
  }, [order, save]);

  const move = (from, to) => {
    if (from === to || from < 0 || to < 0 || to >= order.length) return;
    const next = [...order];
    const [x] = next.splice(from, 1);
    next.splice(to, 0, x);
    commit(next);
  };
  const moveBy = (id, delta) => move(order.indexOf(id), order.indexOf(id) + delta);
  const rowProps = (id) => ({
    draggable: true,
    onDragStart: (e) => { setDragging(id); e.dataTransfer.effectAllowed = "move"; try { e.dataTransfer.setData("text/plain", String(id)); } catch (_) { /* old browsers */ } },
    onDragOver: (e) => { if (dragging == null) return; e.preventDefault(); setOver(id); },
    onDragLeave: () => setOver((o) => (o === id ? null : o)),
    onDrop: (e) => { e.preventDefault(); if (dragging != null) move(order.indexOf(dragging), order.indexOf(id)); setDragging(null); setOver(null); },
    onDragEnd: () => { setDragging(null); setOver(null); },
    "data-dragging": dragging === id ? "true" : undefined,
    "data-over": over === id && dragging !== id ? "true" : undefined,
  });
  return { items: ordered, rowProps, moveBy, index: (id) => order.indexOf(id), count: order.length };
}

/** The handle and the keyboard buttons for one row. */
export function ReorderHandle({ r, id, label }) {
  const i = r.index(id);
  return (
    <span className="ops-reorder">
      <span className="ops-grip" aria-hidden="true" title="Drag to reorder">⠿</span>
      <button type="button" className="ops-icon-btn" aria-label={`Move ${label} up`} disabled={i <= 0} onClick={() => r.moveBy(id, -1)}>↑</button>
      <button type="button" className="ops-icon-btn" aria-label={`Move ${label} down`} disabled={i >= r.count - 1} onClick={() => r.moveBy(id, 1)}>↓</button>
    </span>
  );
}

/* ── Unsaved changes ────────────────────────────────────────── */
/**
 * useUnsaved(dirty) → guard(fn): runs fn, or asks first when there are
 * unsaved changes. Leaving or reloading the page also asks.
 */
export function useUnsaved(dirty) {
  const ref = useRef(dirty);
  ref.current = dirty;
  useEffect(() => {
    const warn = (e) => { if (ref.current) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);
  return useCallback((fn) => () => {
    if (!ref.current || window.confirm("You have unsaved changes. Leave without saving them?")) fn();
  }, []);
}

/** Compares two plain objects field by field (form vs what was loaded). */
export const changed = (a, b) => Object.keys({ ...a, ...b }).some((k) => String(a[k] ?? "") !== String(b[k] ?? ""));

/* ── Gallery ────────────────────────────────────────────────── */
/**
 * <Gallery kind="room" ownerId={12} title="Photos of Nirvana 6" />
 * Each owner's gallery is separate. The primary photo is always first and
 * is what guests see first.
 */
export function Gallery({ kind, ownerId, title = "Photos", hint, canEdit = true, onChange }) {
  const [photos, setPhotos] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const replaceRef = useRef(null);
  const replacing = useRef(null);

  const load = useCallback(() => {
    galleryAPI.list(kind, ownerId).then((r) => setPhotos(r.data.photos)).catch((err) => { setPhotos([]); toast.error(err.message); });
  }, [kind, ownerId]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const r = await fn();
      if (r?.data?.photos) setPhotos(r.data.photos);
      toast.success(r?.message || okMsg);
      onChange?.(r?.data?.photos);
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const r = useReorder(photos || [], async (ids) => {
    const res = await galleryAPI.reorder(kind, ownerId, ids);
    setPhotos(res.data.photos);
    onChange?.(res.data.photos);
  });

  const upload = (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (files.length) run(() => galleryAPI.upload(kind, ownerId, files), "Photos added.");
  };
  const replace = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f && replacing.current) run(() => galleryAPI.replace(kind, ownerId, replacing.current, f), "Photo replaced.");
  };

  return (
    <section className="ops-gallery" aria-label={title} aria-busy={busy}>
      <div className="ops-card-head">
        <div><h4 className="ops-gallery-title">{title}</h4>{hint && <span className="ops-sub">{hint}</span>}</div>
        {canEdit && (
          <>
            <button type="button" className="ops-btn ops-btn--sm" disabled={busy} onClick={() => fileRef.current?.click()}>{busy ? "Working…" : "Add photos"}</button>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/avif" multiple hidden onChange={upload} />
            <input ref={replaceRef} type="file" accept="image/jpeg,image/png,image/webp,image/avif" hidden onChange={replace} />
          </>
        )}
      </div>
      {photos === null ? <p className="ops-muted">Loading photos…</p> : !photos.length ? (
        <p className="ops-note">No photos yet.{canEdit ? " Add several at once; the first becomes the primary photo." : ""}</p>
      ) : (
        <ol className="ops-gallery-grid">
          {r.items.map((p, i) => (
            <li key={p.id} className={`ops-gallery-item ${p.is_primary ? "is-primary" : ""}`} {...(canEdit ? r.rowProps(p.id) : {})}>
              <img src={mediaUrl(p.file_path)} alt={`${title}, ${i + 1} of ${r.items.length}${p.is_primary ? ", primary" : ""}`} loading="lazy" />
              {p.is_primary && <span className="ops-gallery-flag">Primary</span>}
              {canEdit && (
                <div className="ops-gallery-tools">
                  <ReorderHandle r={r} id={p.id} label={`photo ${i + 1}`} />
                  {!p.is_primary && <button type="button" className="ops-btn ops-btn--sm" disabled={busy} onClick={() => run(() => galleryAPI.primary(kind, ownerId, p.id), "Primary photo set.")}>Make primary</button>}
                  <button type="button" className="ops-btn ops-btn--sm" disabled={busy} onClick={() => { replacing.current = p.id; replaceRef.current?.click(); }}>Replace</button>
                  <button type="button" className="ops-btn ops-btn--sm ops-btn--danger" disabled={busy} aria-label={`Remove photo ${i + 1}`}
                    onClick={() => window.confirm("Remove this photo?") && run(() => galleryAPI.remove(kind, ownerId, p.id), "Photo removed.")}>Remove</button>
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
